import { styled } from '@linaria/react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { SessionPullRequest } from '../../../../api/agent-miner-client/agent-miner-client.type';
import { BrandedLoadingOverlay } from '../../../../components/loader/branded-loader';
import { baseWhite, inkLight, secondaryBase } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { H3, Text } from '../../../../ui-library/typography/typography';
import { fmtNum } from '../../shared/formatters';
import { buildLineage } from './pr-survival.helpers';
import { PrCodeView } from './agent-code/pr-code-view';
import { PrContributorsView } from './pr-contributors-view';
import { PrLineage } from './pr-lineage';
import { PR_VIEWS, PrView } from './pull-requests-tab.type';

type PullRequestsTabProps = {
  pullRequests: SessionPullRequest[];
  isLoading: boolean;
  isError: boolean;
  /** Index of the PR whose detail is shown; selection lives in the parent so the side panel and Overview share it. */
  activeIndex: number;
  /** The session whose detail page is open. */
  sessionId: string;
  view: PrView;
  onViewChange: (view: PrView) => void;
  selectedFileId: string | null;
  onSelectFile: (fileId: string) => void;
  /** Whether the side panel (with the file tree) is open; when closed the code view shows its own file picker. */
  panelOpen: boolean;
};

const VIEW_ICONS: Record<PrView, string> = {
  [PrView.Contributors]: 'group',
  [PrView.Code]: 'code',
  [PrView.Lineage]: 'account_tree',
};

/**
 * One-line caption under the section title summarising the active view.
 *
 * @param {SessionPullRequest} pr - The selected pull request
 * @param {PrView} view - The active view
 * @param {string} sessionId - The viewed session (scopes the lineage edit count)
 * @param {TFunction} t - The translation function
 * @returns {string} The caption text
 */
const viewCaption = (pr: SessionPullRequest, view: PrView, sessionId: string, t: TFunction): string => {
  if (view === PrView.Code) {
    const base = String(t('agent_miner.pull_requests.view_captions.code', { files: fmtNum(pr.files.length) }));
    if (!pr.survival.line_precise) return base;
    const kept = String(
      t('agent_miner.pull_requests.view_captions.code_kept', {
        survived: fmtNum(pr.survival.survived),
        authored: fmtNum(pr.survival.authored),
      }),
    );
    return `${base} ${kept}`;
  }
  if (view === PrView.Lineage) {
    const lineage = buildLineage(pr, sessionId);
    return String(
      t('agent_miner.pull_requests.view_captions.lineage', {
        edits: fmtNum(lineage.edits.length),
        files: fmtNum(lineage.files.length),
      }),
    );
  }
  return String(t('agent_miner.pull_requests.view_captions.contributors'));
};

/**
 * The Pull Requests tab body: a section header (view title and caption on the
 * left, view pills on the right), then one view at a time: Contributors (authorship and
 * sessions), Code (tagged diff), or Lineage (edit → file → PR flow). PR
 * selection and the file tree live in the side panel; both are owned by
 * `SessionDetail`, which also shows the PR title and metadata in the Overview panel.
 *
 * @param {PullRequestsTabProps} props - Lifted PR list, flags, selection, and view state
 * @returns {JSX.Element} The pull-requests tab content
 */
export function PullRequestsTab({
  pullRequests,
  isLoading,
  isError,
  activeIndex,
  sessionId,
  view,
  onViewChange,
  selectedFileId,
  onSelectFile,
  panelOpen,
}: PullRequestsTabProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <LoadingWrap>
        <BrandedLoadingOverlay visible variant="colored" />
      </LoadingWrap>
    );
  }

  if (isError) {
    return <Message>{t('agent_miner.pull_requests.error')}</Message>;
  }

  if (pullRequests.length === 0) {
    return <Message>{t('agent_miner.pull_requests.empty')}</Message>;
  }

  const activePr = pullRequests[Math.min(activeIndex, pullRequests.length - 1)];

  const viewPills = (
    <ViewBar>
      {PR_VIEWS.map((candidate) => {
        const active = view === candidate;
        return (
          <Button
            key={candidate}
            size="xs"
            radius="xl"
            variant={active ? 'secondary' : 'outline'}
            aria-pressed={active}
            onClick={() => onViewChange(candidate)}
            leftSection={<Icon name={VIEW_ICONS[candidate]} size={16} color={active ? baseWhite : secondaryBase} />}
          >
            {t(`agent_miner.pull_requests.views.${candidate}`)}
          </Button>
        );
      })}
    </ViewBar>
  );

  const caption = viewCaption(activePr, view, sessionId, t);

  return (
    <Wrap>
      <Header>
        <TitleBlock>
          <H3>{t(`agent_miner.pull_requests.views.${view}`)}</H3>
          <Text size="small" color={inkLight}>
            {caption}
          </Text>
        </TitleBlock>
        {viewPills}
      </Header>
      {view === PrView.Contributors && <PrContributorsView pr={activePr} />}
      {view === PrView.Code && (
        <PrCodeView
          key={activePr.pr_id}
          pr={activePr}
          sessionId={sessionId}
          selectedFileId={selectedFileId}
          onSelectFile={onSelectFile}
          showFilePicker={!panelOpen}
        />
      )}
      {view === PrView.Lineage && <PrLineage key={activePr.pr_id} pr={activePr} sessionId={sessionId} />}
    </Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex: 0 0 auto;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ViewBar = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const LoadingWrap = styled.div`
  position: relative;
  min-height: 160px;
`;

const Message = styled.div`
  padding: 32px 4px;
  font: 500 13px/1.5 'Figtree', sans-serif;
  color: ${inkLight};
`;
